import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import apiClient from 'utils/apiClient';

export interface EventHistoryState {
    histories: EventHistoryProps[]
    loading: boolean
}

export interface EventHistoryProps {
    unique: string
    ts: number
    accountDetailUuid: string
    teamId: number
    eventType: string
    version: number
    data: string
    event: string
}

export const initialState: EventHistoryState = {
    histories: [],
    loading: false
};

export const searchEventHistories = createAsyncThunk<EventHistoryProps[], { accountDetailUuid: string, gteDate?: string }, { state: RootState }>(
    'eventHistory/search',
    async (params, { rejectWithValue }) => {
        const { accountDetailUuid, gteDate } = params;

        let url = `/api/history/event?accountDetailUuid=${encodeURIComponent(accountDetailUuid)}`;
        if (gteDate) url += `&gteDate=${encodeURIComponent(gteDate)}`;

        try {
            const response = await apiClient.get(url);
            return response.data.records;
        } catch (e: any) {
            return rejectWithValue(e?.response?.data?.msg || 'Failed to fetch event history');
        }
    },
    {
        condition: (_, { getState }) => {
            const { loading } = (getState() as RootState).eventHistory as EventHistoryState;
            return !loading;
        }
    }
)

const eventHistorySlice = createSlice({
    name: "eventHistory",
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(searchEventHistories.pending, (state) => {
                state.loading = true;
            })
            .addCase(searchEventHistories.fulfilled, (state, action) => {
                state.loading = false;
                // API returns ASC, reverse to get DESC (newest first)
                state.histories = [...action.payload].reverse();
            })
            .addCase(searchEventHistories.rejected, (state) => {
                state.loading = false;
            })
    },
    reducers: {
        refresh: () => initialState,
    }
})

export const {
    refresh
} = eventHistorySlice.actions;
export default eventHistorySlice.reducer;
